import { Info, Pencil } from 'lucide-react'
import { useState } from 'react'

import { t } from '../../../../../../../common/i18n.js'
import {
    resolveTimeOffCancelAccess,
    resolveTimeOffEditAccess,
} from '../../../../../../../common/time-off-edit-rules.js'
import type { Language } from '../../../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../../../common/utils/format-utils.js'
import { getLeaveBadgeEmoji } from '../../../../../../../common/utils/leave-badge-emoji.js'
import { Button } from '../../../../../../components/button/button.js'
import { TextField } from '../../../../../../components/forms/text-field/text-field.js'
import { Tooltip } from '../../../../../../components/tooltip/tooltip.js'
import type { LeaveRequest } from '../../employee-profile-types.js'
import type { YearListedRequest } from './time-away-derive.js'

interface TimeAwayRequestListProps {
    requests: YearListedRequest[]
    variant: 'upcoming' | 'history'
    lang: Language
    // Cancel wiring — upcoming variant only. The orchestrator wraps the
    // confirm dialog; this list only decides per-row visibility.
    isSelf?: boolean
    // Server-resolved time-away manage capability for this target (approve /
    // edit others' requests). Replaces the old isAdmin/isDirectManager flags.
    canManage?: boolean
    today?: string
    onCancelRequest?: (requestId: number) => void
    // Opens the edit modal (same flow as booking, with overlap warnings);
    // the orchestrator owns the form and the PUT.
    onStartEdit?: (request: LeaveRequest) => void
    // Approve/reject a pending request (managers/admins, not on own profile).
    onDecideRequest?: (
        requestId: number,
        action: 'approve' | 'reject',
        rejectionReason?: string,
    ) => Promise<boolean>
    // Approve/reject an owner's retroactive deletion request (TASK-402) —
    // managers/admins, not on own profile.
    onDecideCancellation?: (requestId: number, action: 'approve' | 'reject') => Promise<boolean>
}

// Shared row renderer for the Upcoming card and the per-year history cards:
// emoji | type | length | dates | note, plus a badge for pending requests.
export const TimeAwayRequestList = ({
    requests,
    variant,
    lang,
    isSelf,
    canManage,
    today,
    onCancelRequest,
    onStartEdit,
    onDecideRequest,
    onDecideCancellation,
}: TimeAwayRequestListProps) => {
    const tOff = t.portal.timeOff

    // Approve/reject state — `rejectingId` flips a row into the reason form,
    // `actionLoadingId` disables its buttons while the request is in flight.
    const [rejectingId, setRejectingId] = useState<number | null>(null)
    const [rejectReason, setRejectReason] = useState('')
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null)
    // Year-spanning rows show a per-year day split behind an ⓘ (Humaans-style).
    const [openSplitId, setOpenSplitId] = useState<number | null>(null)

    const handleDecide = async (requestId: number, action: 'approve' | 'reject') => {
        if (!onDecideRequest) return
        setActionLoadingId(requestId)
        const ok = await onDecideRequest(
            requestId,
            action,
            action === 'reject' ? rejectReason : undefined,
        )
        if (ok) {
            setRejectingId(null)
            setRejectReason('')
        }
        setActionLoadingId(null)
    }

    const handleDecideCancellation = async (requestId: number, action: 'approve' | 'reject') => {
        if (!onDecideCancellation) return
        setActionLoadingId(requestId)
        await onDecideCancellation(requestId, action)
        setActionLoadingId(null)
    }

    return (
        <div className="time-away__req-list">
            {requests.map((req) => {
                const cancelRequested = req.cancel_requested_at_utc !== null

                // Shared server/client rules (TASK-402): manage rights cancel
                // any row — taken absences included; owners cancel their own
                // pending/upcoming directly and taken approval-flow ones via a
                // deletion request the manager confirms.
                const cancelAccess =
                    today !== undefined
                        ? resolveTimeOffCancelAccess({
                              isSelf: isSelf === true,
                              canManageTarget: canManage === true && isSelf !== true,
                              status: req.status,
                              startDate: req.start_date,
                              today,
                              requiresApproval: req.requiresApproval,
                              cancelRequested,
                          })
                        : undefined
                const canCancel = onCancelRequest !== undefined && cancelAccess?.allowed === true

                // Shared server/client rules: owners and managers/admins edit
                // any PENDING/APPROVED row — an owner's retroactive edit of an
                // approval-flow request goes back to the manager.
                const canEdit =
                    onStartEdit !== undefined &&
                    today !== undefined &&
                    resolveTimeOffEditAccess({
                        isSelf: isSelf === true,
                        canManageTarget: canManage === true && isSelf !== true,
                        status: req.status,
                        endDate: req.end_date,
                        today,
                    }).allowed

                // Managers/admins can decide a PENDING request — but not on their
                // own profile (the server blocks self-approval anyway).
                const canDecide =
                    variant === 'upcoming' &&
                    onDecideRequest !== undefined &&
                    canManage === true &&
                    isSelf !== true &&
                    req.status === 'PENDING'

                // Managers/admins decide an owner's pending deletion request.
                // When shown, it replaces the generic cancel button — approving
                // the deletion IS the cancellation.
                const canDecideCancellation =
                    onDecideCancellation !== undefined &&
                    canManage === true &&
                    isSelf !== true &&
                    cancelRequested &&
                    req.status === 'APPROVED'

                const isRejecting = rejectingId === req.id

                return (
                    <div key={req.id} className="time-away__req-row">
                        <span className="time-away__req-emoji">
                            {getLeaveBadgeEmoji(req.badgeType)}
                        </span>
                        <span className="time-away__req-type">{req.leaveTypeName}</span>
                        <span className="time-away__req-days">
                            {tOff.view.dayCount(req.yearDays ?? req.business_days)(lang)}
                            {req.yearSplit && (
                                <Tooltip
                                    isShown={openSplitId === req.id}
                                    onToggle={(shown) =>
                                        setOpenSplitId((prev) =>
                                            shown ? req.id : prev === req.id ? null : prev,
                                        )
                                    }
                                    triggerEvent="hover"
                                    color="white"
                                    placement="bottom"
                                    className="time-away__split-tooltip"
                                    triggerClassName="time-away__info"
                                    trigger={<Info size={13} />}
                                >
                                    <div>
                                        {req.yearSplit.map((part) => (
                                            <div key={part.year} className="time-away__split-line">
                                                {tOff.view.daysInPeriod(part.days, part.year)(lang)}
                                            </div>
                                        ))}
                                    </div>
                                </Tooltip>
                            )}
                        </span>
                        <span className="time-away__req-range">
                            {req.start_date === req.end_date
                                ? formatDisplayDate(req.start_date)
                                : `${formatDisplayDate(req.start_date)} → ${formatDisplayDate(req.end_date)}`}
                        </span>
                        <span className="time-away__req-note">{req.note ?? '–'}</span>
                        {/* Fixed-width slot on every row (history rows are always
                            APPROVED) so a pending badge doesn't squeeze this row's
                            flexible columns out of line with its neighbours. A
                            history row only grows the slot while its deletion
                            request is open — the badge is the row's whole point
                            then. */}
                        {(variant === 'upcoming' || cancelRequested) && (
                            <span className="time-away__req-status-slot">
                                {cancelRequested ? (
                                    <span className="time-away__req-status">
                                        {tOff.status.cancelRequested(lang)}
                                    </span>
                                ) : (
                                    req.status === 'PENDING' && (
                                        <span className="time-away__req-status">
                                            {tOff.status.pending(lang)}
                                        </span>
                                    )
                                )}
                            </span>
                        )}
                        {/* One reserved right slot for every action variant, so a
                            row without a cancel button (e.g. no-approval leave)
                            keeps the same column grid as its neighbours. */}
                        <span className="time-away__req-actions">
                            {canEdit && (
                                <Button
                                    variant="tertiary"
                                    size="small"
                                    iconLeft={Pencil}
                                    text={tOff.edit(lang)}
                                    onClick={() => onStartEdit(req)}
                                />
                            )}
                            {canCancel && !canDecideCancellation && (
                                <Button
                                    variant="tertiary"
                                    size="small"
                                    text={tOff.cancel(lang)}
                                    onClick={() => onCancelRequest(req.id)}
                                />
                            )}
                            {canDecideCancellation && (
                                <div className="time-away__req-decide">
                                    <Button
                                        variant="alert"
                                        size="small"
                                        text={tOff.approveCancellation(lang)}
                                        isDisabled={actionLoadingId === req.id}
                                        onClick={() =>
                                            void handleDecideCancellation(req.id, 'approve')
                                        }
                                    />
                                    <Button
                                        variant="tertiary"
                                        size="small"
                                        text={tOff.rejectCancellation(lang)}
                                        isDisabled={actionLoadingId === req.id}
                                        onClick={() =>
                                            void handleDecideCancellation(req.id, 'reject')
                                        }
                                    />
                                </div>
                            )}
                            {canDecide &&
                                (isRejecting ? (
                                    <div className="time-away__req-decide">
                                        <TextField
                                            as="textarea"
                                            className="time-away__req-reject-input"
                                            value={rejectReason}
                                            onChange={(value) => setRejectReason(value)}
                                            placeholder={tOff.rejectionReason(lang)}
                                            textAreaProps={{ rows: 2 }}
                                        />
                                        <Button
                                            variant="tertiary"
                                            size="small"
                                            text={tOff.cancel(lang)}
                                            onClick={() => {
                                                setRejectingId(null)
                                                setRejectReason('')
                                            }}
                                        />
                                        <Button
                                            variant="alert"
                                            size="small"
                                            text={tOff.reject(lang)}
                                            isDisabled={actionLoadingId === req.id}
                                            onClick={() => void handleDecide(req.id, 'reject')}
                                        />
                                    </div>
                                ) : (
                                    <div className="time-away__req-decide">
                                        <Button
                                            variant="success"
                                            size="small"
                                            text={tOff.approve(lang)}
                                            isDisabled={actionLoadingId === req.id}
                                            onClick={() => void handleDecide(req.id, 'approve')}
                                        />
                                        <Button
                                            variant="alert"
                                            size="small"
                                            text={tOff.reject(lang)}
                                            onClick={() => setRejectingId(req.id)}
                                        />
                                    </div>
                                ))}
                        </span>
                    </div>
                )
            })}
        </div>
    )
}
